import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import type {
  TokenPotentialDiscount,
  TokenPotentialDiscountVariables,
} from './__generated__/usePotentialDiscount.graphql';

const TOKEN_POTENTIAL_DISCOUNT_QUERY = gql`
  query TokenPotentialDiscount(
    $amount: String!
    $currency: SupportedCurrency!
    $sport: Sport!
    $exchangeRateId: ID!
    $auctionId: ID
  ) {
    market {
      id
      potentialDiscount(
        amount: { amount: $amount, currency: $currency }
        exchangeRateId: $exchangeRateId
        auctionId: $auctionId
        sport: $sport
      )
    }
  }
` as TypedDocumentNode<TokenPotentialDiscount, TokenPotentialDiscountVariables>;

export const usePotentialDiscount = (
  monetaryAmount: MonetaryAmount,
  { auctionId }: { auctionId?: string } = {}
): MonetaryAmount | undefined => {
  const {
    flags: { nonStackableMarketCredits = false },
  } = useFeatureFlags();
  const { exchangeRate } = useConfigContext();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(TOKEN_POTENTIAL_DISCOUNT_QUERY, {
    variables: {
      amount: monetaryAmount.referenceValue.toString(),
      currency: monetaryAmount.referenceCurrency as SupportedCurrency,
      exchangeRateId: idFromObject(exchangeRate.id),
      auctionId,
      sport,
    },
    skip:
      !monetaryAmount ||
      monetaryAmount.isZero ||
      !exchangeRate.id ||
      !nonStackableMarketCredits,
  });

  const potentialDiscount = useMemo(() => {
    const discount = data?.market.potentialDiscount;
    return discount
      ? MonetaryAmount.fromReferenceCurrency(
          monetaryAmount.referenceCurrency,
          discount,
          exchangeRate.ethRates
        )
      : MonetaryAmount.zero;
  }, [
    data?.market.potentialDiscount,
    monetaryAmount.referenceCurrency,
    exchangeRate.ethRates,
  ]);

  if (loading) {
    return undefined;
  }

  return potentialDiscount;
};
